<template>
  <div>
    <b-notification
        v-model="isError"
        type="is-danger"
        aria-close-label="Close notification"
        :closable="false">
        <b>Error fetching Meal Plan Collection!</b><br />Try refreshing the page. If the error persists check your network connection or let BM know.
    </b-notification>
    <b-loading :is-full-page="true" v-model="isLoading"></b-loading>
    <Keypress key-event="keyup" :modifiers="['ctrlKey']" :key-code="83" :preventDefault="true" @success="save" />

    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      v-if="!isLoading && !isError"
      parentTitle="Meal Plan Collections"
      :currentTitle="title"
      :backRoute="{ name: 'MealPlanCollectionListView' }"
    >
      <FeedsStatusBadge :status="mealPlanCollection.status" />
      <b-button outlined type="is-primary" size="is-small" icon-left="check" @click="saveAndClose">Save & Close</b-button>
      <b-button type="is-primary" size="is-small" icon-left="content-save" @click="save">Save</b-button>
    </TopBar>

    <div v-if="!isLoading && !isError" class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ title }}</h1>
          <p class="page-subtitle">Meal Plan Collection Editor</p>
        </div>
      </div>

      <!-- Top Grid: General + Images -->
      <div class="top-grid">
        <FeedsCard title="General Information">
          <div class="form-group">
            <label class="form-label">Status</label>
            <b-select v-model="mealPlanCollection.status" placeholder="Status" expanded @input="save">
              <option>draft</option>
              <option>test</option>
              <option>fix</option>
              <option>live</option>
            </b-select>
          </div>
          <div class="form-group">
            <label class="form-label">Name</label>
            <b-input v-model="mealPlanCollection.title" @input="saveDebounced" />
          </div>
          <div class="form-group">
            <label class="form-label">Description</label>
            <b-input maxlength="100" type="textarea" v-model="mealPlanCollection.description" @input="saveDebounced"/>
          </div>
          <div class="form-group">
            <label class="form-label">Position</label>
            <b-numberinput
              v-model="mealPlanCollection.position"
              placeholder="e.g. 1"
              type="is-light"
              controls-position="compact"
              :min="1"
              @input="saveDebounced">
            </b-numberinput>
          </div>
        </FeedsCard>

        <FeedsCard title="Images">
          <template #header-actions>
            <b-button size="is-small" type="is-primary" icon-left="plus" @click="$refs.editImages.openAddImage()">Add Image</b-button>
          </template>
          <EditImages
            ref="editImages"
            v-model="mealPlanCollection.images"
            @input="save"
          />
        </FeedsCard>
      </div>

      <!-- Meal Plans Section -->
      <FeedsCard title="Meal Plans" class="mt-5">
        <EditMealPlans
          :maxPlans="20"
          title=""
          v-model="mealPlanCollection.plans"
          @input="save"
          @plan-added="planAdded($event)"
          @plan-removed="planRemoved($event)"
        />
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import EditImages from "@/components/EditImages"
import EditMealPlans from "@/components/EditMealPlans"
import db from "@/firebase/db"
import firebase from 'firebase/app'
import FeedsCard from "@/components/common/FeedsCard"
import FeedsStatusBadge from "@/components/common/FeedsStatusBadge"
import TopBar from "@/components/layout/TopBar"

export default {
  name: 'MealPlanCollectionView',
  components: {
    EditImages,
    EditMealPlans,
    FeedsCard,
    FeedsStatusBadge,
    TopBar,
    Keypress: () => import('vue-keypress'),
  },
  props: [
    'id'
  ],
  data() {
    return {
      isLoading: true,
      isError: false,
      mealPlanCollection: {}
    };
  },
  computed: {
    title() {
      return this.mealPlanCollection.title ?? "New Meal Plan Collection"
    },
    image() {
      if (this.mealPlanCollection.images == null) {
        return null;
      }
      return this.mealPlanCollection.images.length > 0 ? this.mealPlanCollection.images[0].src : null
    },
    displayPlans() {
      var plans = this.mealPlanCollection.plans ?? {};
      var array = Object.keys(plans).map(function(planID) {
        var newObj = {id: planID};
        for (var property in plans[planID]) {
          newObj[property] = plans[planID][property];
        }
        return newObj;
      });
      return array.sort((a, b) => a.position - b.position);
    }
  },
  created: function () {
    db.collection('mealplan_collections')
      .doc(this.id)
      .get()
      .then(snapshot => {
        if (snapshot.exists) {
          this.mealPlanCollection = snapshot.data();
        } else {
          this.isError = true;
        }
        this.isLoading = false;
      })
      .catch((error) => {
        this.isError = true;
        this.$buefy.notification.open({type: 'is-danger', message: `Error fetching Meal Plan Collection: ${error}`})
      });
  },
  methods: {
    saveDebounced: debounce(function () {
      this.save()
    }, 1000),
    async saveAndClose(){
      this.save()
        .then(() => {
          this.$router.go(-1)
        })
    },
    async save(){
      db.collection('mealplan_collections')
        .doc(this.id)
        .set(this.mealPlanCollection)
        .then(() => {
          this.$buefy.toast.open({type: 'is-success', message: `Meal plan Collection successfully updated!`})
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error saving Meal plan Collection: ${error}`})
        });
    },
    async planAdded(plan){
      const entry = {};
      entry[`${this.id}`] = true;
      db.collection("mealplan_mealplan_collections").doc(plan.id)
        .set(entry, {merge: true})
        .catch((error) => {
          console.error("Error adding mealplan_mealplan_collections entry: ", error);
        });
    },
    async planRemoved(plan){
      const remove = {};
      remove[`${this.id}`] = firebase.firestore.FieldValue.delete();
      db.collection("mealplan_mealplan_collections").doc(plan.id)
        .update(remove)
        .catch((error) => {
          console.error("Error removing mealplan_mealplan_collections entry: ", error);
        });
    },
  }
}
</script>

<style scoped>
.top-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 24px;
  align-items: start;
}
</style>
